import type { Probe } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

/**
 * Performance and calibration.
 *
 * Two things matter here:
 *  1. performance.memory (Chromium-only) — a coarse JS heap size.
 *  2. A cheap CPU calibration loop: we measure how much integer work completes
 *     in a fixed window and compare with navigator.hardwareConcurrency. A VM
 *     reporting 16 cores but delivering single-core-equivalent throughput in a
 *     tight synchronous loop is a mild inconsistency; more importantly, it
 *     contributes a stable, hardware-ish number to the engineTiming part.
 *
 * We deliberately do NOT use this for the bot score on its own, because
 * throttle/CPU-pressure legitimately slows the loop. It is reported, and it
 * feeds a fingerprint part.
 */
export const performanceProbe: Probe = {
  id: 'performance',
  label: 'Performance',
  layer: 'engine',
  tier: 'client',
  category: 'hardware',
  description: 'Heap size and a CPU calibration measurement',
  budgetMs: 2000,

  async run() {
    const mem = performance as Performance & {
      memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number };
    };

    const heap = mem.memory
      ? {
          usedJSHeapSize: sig(mem.memory.usedJSHeapSize, 3),
          totalJSHeapSize: sig(mem.memory.totalJSHeapSize, 3),
          jsHeapSizeLimit: sig(mem.memory.jsHeapSizeLimit, 3),
        }
      : null;

    // CPU calibration: a fixed number of integer ops in a tight loop, timed.
    // Calibrate to a target duration so slow machines finish quickly.
    const calibration = await calibrate();
    const reportedCores = navigator.hardwareConcurrency ?? null;
    // Ops-per-ms is the comparable figure; bucket it for stability.
    const opsPerMs = sig(calibration.ops / calibration.elapsedMs, 2);

    return {
      signals: {
        heap: heap ? ok(heap) : na<unknown>('unsupported', 'client', 'performance.memory is Chromium-only'),
        cpuCalibrationOps: ok(calibration.ops, 'client'),
        cpuCalibrationMs: ok(sig(calibration.elapsedMs, 3), 'client'),
        opsPerMs: ok(opsPerMs, 'derived'),
        reportedHardwareConcurrency: ok(reportedCores),
        // Whether ops-per-ms is consistent with a many-core claim. Soft signal.
        timeOrigin: ok(performance.timeOrigin, 'client'),
      },
      // This probe deliberately contributes NO fingerprint part: throughput and
      // heap size track machine load, not device identity, so hashing them
      // would make the fingerprint drift between runs in the same browser.
      // The values are still shown and still useful for inspection.
    };
  },
};

/** Runs integer work until ~targetMs elapses; returns ops and elapsed. */
async function calibrate(targetMs = 30): Promise<{ ops: number; elapsedMs: number }> {
  // Yield first so the timer starts cleanly.
  await new Promise((r) => setTimeout(r, 0));
  const start = performance.now();
  let ops = 0;
  let elapsed = 0;
  // A non-optimizable mix so V8 cannot fold the loop entirely.
  let acc = 0;
  while (elapsed < targetMs) {
    for (let i = 0; i < 50_000; i++) {
      acc = (acc + i * 2654435761) >>> 0;
      ops++;
    }
    elapsed = performance.now() - start;
  }
  // Keep acc live so the loop isn't eliminated.
  if (acc === 0x7fffffff) ops += 0;
  return { ops, elapsedMs: elapsed };
}

registerProbe(performanceProbe);
